import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const STORAGE_PREFIX = 'loadfetcher:searchTabs'
const STORAGE_VERSION = 'v1'
export const MAX_TABS = 10
const PERSIST_DEBOUNCE_MS = 400

const storageKey = boardMode => `${STORAGE_PREFIX}:${boardMode}:${STORAGE_VERSION}`

const newTabId = () => {
	if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
	const rand =
		typeof crypto !== 'undefined' && crypto.getRandomValues
			? Array.from(crypto.getRandomValues(new Uint8Array(8)))
					.map(b => b.toString(16).padStart(2, '0'))
					.join('')
			: Math.random().toString(36).slice(2, 18)
	return `t-${Date.now().toString(36)}-${rand}`
}

const makeTab = filters => ({ id: newTabId(), filters })

const isObject = v => v != null && typeof v === 'object' && !Array.isArray(v)

const loadFromStorage = (boardMode, baseFilters) => {
	try {
		const raw = localStorage.getItem(storageKey(boardMode))
		if (!raw) return null
		const parsed = JSON.parse(raw)
		if (!parsed || parsed.mode !== boardMode || !Array.isArray(parsed.tabs)) return null
		const tabs = parsed.tabs
			.filter(t => isObject(t) && isObject(t.filters))
			.map(t => ({
				id: typeof t.id === 'string' && t.id ? t.id : newTabId(),
				filters: { ...baseFilters, ...t.filters },
			}))
		if (tabs.length === 0) return null
		const activeTabId = tabs.some(t => t.id === parsed.activeTabId)
			? parsed.activeTabId
			: tabs[0].id
		return { tabs, activeTabId }
	} catch {
		return null
	}
}

// Per-board-mode persistence isolates Amazon-relay tabs from any future
// board variants (e.g. mixed sources) so a schema or behavior change in one
// can't poison the other.
export default function useSearchTabs(
	initialFilters,
	boardMode = 'amazon',
	{ persist = true } = {},
) {
	const persistEnabled = persist !== false
	const persistEnabledRef = useRef(persistEnabled)
	persistEnabledRef.current = persistEnabled

	const [{ tabs, activeTabId }, setState] = useState(() => {
		const stored = persistEnabled ? loadFromStorage(boardMode, initialFilters) : null
		if (stored) return stored
		const first = makeTab(initialFilters)
		return { tabs: [first], activeTabId: first.id }
	})

	const persistTimerRef = useRef(null)
	const latestStateRef = useRef({ tabs, activeTabId })
	useEffect(() => {
		latestStateRef.current = { tabs, activeTabId }
	}, [tabs, activeTabId])

	const flushPersist = useCallback(() => {
		if (persistTimerRef.current) {
			clearTimeout(persistTimerRef.current)
			persistTimerRef.current = null
		}
		if (!persistEnabledRef.current) return
		try {
			localStorage.setItem(
				storageKey(boardMode),
				JSON.stringify({ mode: boardMode, ...latestStateRef.current }),
			)
		} catch {
			// localStorage can fail in private mode / over quota — non-fatal.
		}
	}, [boardMode])

	useEffect(() => {
		if (persistTimerRef.current) clearTimeout(persistTimerRef.current)
		if (!persistEnabled) return undefined
		persistTimerRef.current = setTimeout(flushPersist, PERSIST_DEBOUNCE_MS)
		return () => {
			if (persistTimerRef.current) clearTimeout(persistTimerRef.current)
		}
	}, [tabs, activeTabId, flushPersist, persistEnabled])

	useEffect(() => {
		window.addEventListener('pagehide', flushPersist)
		return () => {
			window.removeEventListener('pagehide', flushPersist)
			flushPersist()
		}
	}, [flushPersist])

	const [error, setError] = useState(null)
	const errorTimerRef = useRef(null)
	const showError = useCallback(msg => {
		if (errorTimerRef.current) clearTimeout(errorTimerRef.current)
		setError(msg)
		errorTimerRef.current = setTimeout(() => setError(null), 3000)
	}, [])
	useEffect(
		() => () => {
			if (errorTimerRef.current) clearTimeout(errorTimerRef.current)
		},
		[],
	)

	const addTab = useCallback(() => {
		setState(prev => {
			if (prev.tabs.length >= MAX_TABS) {
				showError(`Up to ${MAX_TABS} searches allowed`)
				return prev
			}
			const fresh = makeTab(initialFilters)
			return { tabs: [...prev.tabs, fresh], activeTabId: fresh.id }
		})
	}, [initialFilters, showError])

	const closeTab = useCallback(
		tabId => {
			setState(prev => {
				const idx = prev.tabs.findIndex(t => t.id === tabId)
				if (idx === -1) return prev
				const remaining = prev.tabs.filter(t => t.id !== tabId)
				if (remaining.length === 0) {
					const fresh = makeTab(initialFilters)
					return { tabs: [fresh], activeTabId: fresh.id }
				}
				let nextActiveId = prev.activeTabId
				if (prev.activeTabId === tabId) {
					const fallback = remaining[idx - 1] || remaining[0]
					nextActiveId = fallback.id
				}
				return { tabs: remaining, activeTabId: nextActiveId }
			})
		},
		[initialFilters],
	)

	const setActiveTab = useCallback((tabId, prepareFilters) => {
		setState(prev => {
			if (prev.activeTabId === tabId) return prev
			if (!prepareFilters) return { ...prev, activeTabId: tabId }

			const idx = prev.tabs.findIndex(t => t.id === tabId)
			if (idx === -1) return prev

			const currentFilters = prev.tabs[idx].filters
			const nextFilters = prepareFilters(currentFilters)
			if (nextFilters === currentFilters) return { ...prev, activeTabId: tabId }

			const nextTabs = prev.tabs.slice()
			nextTabs[idx] = { ...nextTabs[idx], filters: nextFilters }
			return { ...prev, tabs: nextTabs, activeTabId: tabId }
		})
	}, [])

	// Mirrors React's setState API so existing setFilters call sites — both
	// `setFilters({...})` and `setFilters(prev => ({...}))` — work unchanged.
	const updateActiveTabFilters = useCallback(updaterOrValue => {
		setState(prev => {
			const idx = prev.tabs.findIndex(t => t.id === prev.activeTabId)
			if (idx === -1) return prev
			const current = prev.tabs[idx].filters
			const next =
				typeof updaterOrValue === 'function' ? updaterOrValue(current) : updaterOrValue
			if (next === current) return prev
			const nextTabs = prev.tabs.slice()
			nextTabs[idx] = { ...nextTabs[idx], filters: next }
			return { ...prev, tabs: nextTabs }
		})
	}, [])

	const activeTab = useMemo(
		() => tabs.find(t => t.id === activeTabId) || tabs[0],
		[tabs, activeTabId],
	)

	return {
		tabs,
		activeTab,
		activeTabId,
		addTab,
		closeTab,
		setActiveTab,
		updateActiveTabFilters,
		error,
	}
}
